import {type PropsWithChildren, useRef, useState} from "react";
import Konva from "konva";
import {
    SelectionRectangleContext,
    type SelectionRectangle,
} from "@/tabletop/selection/SelectionRectangleHooks.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {combatantsInSelectionRectangle} from "./SelectionRectangleGeometry.ts";

export default function SelectionRectangleProvider({children}: PropsWithChildren) {
    const {combatants} = useCombatants();
    const {selectedCombatants, selectCombatants} = useTableTopSelection();
    const {mapTransform} = useEncounterViewPort();
    const {dungeonSettings} = useDungeon();
    const selectionBaseIds = useRef<string[]>([]);

    const [selectionRectangle, setSelectionRectangle] = useState<SelectionRectangle>({
        visible: false,
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
    });

    const handleStartSelection = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (event.evt.buttons !== 1 || !event.target.hasName("LayerClickTarget")) return;

        const shouldExtendSelection = event.evt.shiftKey || event.evt.ctrlKey || event.evt.metaKey;
        selectionBaseIds.current = shouldExtendSelection
            ? selectedCombatants.map(combatant => combatant.id)
            : [];
        if (!shouldExtendSelection) selectCombatants([]);

        const position = event.target.getStage()?.getPointerPosition();
        if (position) setSelectionRectangle({
            visible: true,
            x1: position.x,
            y1: position.y,
            x2: position.x,
            y2: position.y,
        });
    };

    const handleMoveSelection = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (!selectionRectangle.visible) return;
        if (event.evt.buttons !== 1) {
            setSelectionRectangle({...selectionRectangle, visible: false});
            return;
        }

        const position = event.target.getStage()?.getPointerPosition();
        if (position) setSelectionRectangle({
            ...selectionRectangle,
            x2: position.x,
            y2: position.y,
        });
    };

    const handleEndSelection = () => {
        if (!selectionRectangle.visible) return;

        setSelectionRectangle({...selectionRectangle, visible: false});
        const selected = combatantsInSelectionRectangle(
            combatants, dungeonSettings.levelId, selectionRectangle, mapTransform, selectionBaseIds.current,
        );
        if (selected) selectCombatants(selected);
    };

    const value = {
        selectionRectangle,
        setSelectionRectangle,
        handleStartSelection,
        handleMoveSelection,
        handleEndSelection,
    };

    return (
        <SelectionRectangleContext.Provider value={value}>
            {children}
        </SelectionRectangleContext.Provider>
    );
}
